import Link from 'next/link';
import React from 'react';
import Pill from '@/components/UI/Pill';
import { AvatarSize } from '@/components/cards/Avatar';
import AvatarGroup, {
  convertDealsToAvatarItems,
} from '@/components/cards/AvatarGroup';
import AvatarInfo from '@/components/cards/AvatarInfo';
import {
  BudgetDisplayMode,
  BudgetPerAsset,
} from '@/components/display/BudgetPerAsset';
import ArrowButton, {
  ArrowDirection,
} from '@/components/navigation/ArrowButton';
import { DATE_FORMATS } from '@/constants/constants';
import type {
  CollectionDeals,
  DealFragment,
} from '@/utilities/__generated__/graphql';
import { BucketType, createBucketURL } from '@/utilities/createBucketUrl';
import {
  DateThemeMap,
  getHumanizedDate,
  getLocalDate,
} from '@/utilities/dateFormatter';
import { cleanAddress } from '@/utilities/functions';

const DealTableRow = ({ deal }: { deal: DealFragment }) => {
  const localBiddingExpDate = getLocalDate(
    deal.bidding_expiration,
    DATE_FORMATS.MMMDYhm
  );

  const [humanizedLocalBiddingExpDate, dateEnum] =
    getHumanizedDate(localBiddingExpDate);
  const { theme, pulse } = DateThemeMap[dateEnum];
  return (
    <Link href={`/deal/${deal.id}`}>
      <div className="grid w-full grid-cols-8 rounded-lg px-0 py-4 hover:cursor-pointer md:grid-cols-12 md:px-4 md:hover:bg-primary-surface">
        <div className="col-span-4 cursor-pointer md:col-span-6 lg:col-span-4">
          <AvatarInfo
            size={AvatarSize.sm}
            address={
              deal.Brand.wallet_address
                ? cleanAddress(deal.Brand.wallet_address)
                : ''
            }
            imageLink={`/brand/${encodeURI(deal.Brand.username ?? '')}`}
            textLink={`/deal/${deal.id}`}
            image={
              deal.Brand.username
                ? createBucketURL(
                    BucketType.Brand,
                    deal.Brand.username,
                    deal.Brand.updated_at
                  )
                : ''
            }
            verified={false}
            contentClasses="text-sm"
            alt={
              deal.Brand.company_name
                ? `${deal.Brand.company_name} brand icon`
                : 'brand icon'
            }
            title={deal.title}
            content={
              (deal.Brand?.company_name &&
                `${deal.Brand?.company_name
                  .charAt(0)
                  .toUpperCase()}${deal.Brand?.company_name.slice(1)}`) ??
              ''
            }
          />
        </div>
        <div className="col-span-4 flex justify-end md:col-span-5 md:justify-start lg:col-span-3">
          <AvatarGroup
            size={AvatarSize.sm}
            data={convertDealsToAvatarItems(
              deal.CollectionDeals as Partial<CollectionDeals>[]
            )}
            linkTemplate="/collection/{0}"
          />
        </div>
        <div className="start hidden self-center pr-3 text-left text-black md:col-span-2 lg:flex">
          <BudgetPerAsset
            paymentCurrency={deal.payment_currency}
            minBudget={deal.min_budget}
            maxBudget={deal.max_budget}
            royaltyPercentage={deal.royalty_percentage}
            displayMode={BudgetDisplayMode.WITHOUT_TITLE}
            classes="mx-2"
          />
        </div>
        <div className="start hidden self-center md:col-span-2 lg:flex">
          <Pill
            theme={theme}
            showDot={true}
            pulse={pulse}
            title={localBiddingExpDate}
            classes="mt-0"
          >
            {humanizedLocalBiddingExpDate}
          </Pill>
        </div>
        <div className="col-span-1 hidden h-10 w-10 self-center justify-self-end rounded-full bg-white md:flex">
          <ArrowButton
            direction={ArrowDirection.right}
            classes="text-primary-main"
          />
        </div>
      </div>
    </Link>
  );
};
export default DealTableRow;
